import { useRef, useEffect, useCallback } from 'react';
import { Search, Loader2, X } from 'lucide-react';
import { useFeature } from '~/modules/root';
import { BOARD_TERMS } from '~/config/mood-board-terminology';

interface FollowSelectorSearchProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    autoFocus?: boolean;
    isSearching?: boolean;
}

/**
 * Search input for the FollowSelector.
 */
export const FollowSelectorSearch = ({
    value,
    onChange,
    placeholder,
    autoFocus = true,
    isSearching = false,
}: FollowSelectorSearchProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const isBuildHubEnabled = useFeature('buildhub');
    const defaultPlaceholder = isBuildHubEnabled
        ? 'Search Projects, Boards, Schedules...'
        : BOARD_TERMS.searchBoards;

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            // Small delay to ensure popover is fully rendered
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [autoFocus]);

    const handleClear = () => {
        onChange('');
        inputRef.current?.focus();
    };

    /**
     * On mobile, when the keyboard opens the browser may scroll the input out of view.
     * This handler ensures the input stays visible by scrolling it into view after focus.
     */
    const handleFocus = useCallback(() => {
        if (inputRef.current) {
            setTimeout(() => {
                inputRef.current?.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth',
                });
            }, 300);
        }
    }, []);

    return (
        <div className="shrink-0 px-4.5 pb-4.5">
            <div className="relative flex items-center">
                <Search className="absolute left-3 h-4 w-4 text-gray-400" />
                <input
                    ref={inputRef}
                    type="text"
                    placeholder={placeholder ?? defaultPlaceholder}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={handleFocus}
                    className="focus:outline-hidden h-10 w-full rounded-full border border-gray-300 bg-white pl-9 pr-10 text-[16px] text-gray-900 placeholder:text-gray-400 focus:border-gray-400 focus:ring-0 md:text-sm"
                />
                {isSearching ? (
                    <Loader2 className="absolute right-3 h-4 w-4 animate-spin text-gray-400" />
                ) : value ? (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="absolute right-3 rounded-full p-0.5 text-gray-400 transition-colors hover:text-gray-900"
                        aria-label="Clear search"
                    >
                        <X className="h-4 w-4" />
                    </button>
                ) : null}
            </div>
        </div>
    );
};
